import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LeftOutlined, PlusOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";

import BaseModal from "@/components_v2/Modal/BaseModal";
import BaseTabs from "@/components_v2/Tabs/BaseTabs";
import BaseButton from "@/components_v2/Button/BaseButton";
import { useGetDataTemplatesQuery } from "@/redux/service";
import RequestsTable from "@/domains/evidence-requests/components/RequestsTable";
import RequestForm from "@/domains/evidence-requests/components/RequestForm";
import EvidenceTable from "@/domains/evidence/components/EvidenceTable";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import { useDataBlockPermissions } from "@/domains/datablocks/hooks/useDataBlockPermissions";
import { useDataBlockRequestForm } from "@/features/evidence-requests/hooks/useDataBlockRequestsForm";
import DataBlockInviteIcon from "@/assets/icons/datablock-invites.svg?react";
import EditRequestForm from "@/domains/evidence-requests/components/EditRequestForm";
import useEditDataBlockRequestForm from "@/features/evidence-requests/hooks/useEditDataBlockRequestForm";
import BaseTooltip from "@/components_v2/Tooltip/BaseTooltip";
import CreateDataBlock from "@/domains/datablocks/components/CreateDatablock";
import { useResponsive } from "@/shared/hooks/useBreakpoints";
import ReceiveInviteIcon from "@/assets/icons/receive-invite.svg?react";
import {
  DataBlockResponse,
  DataTemplateResponse,
} from "@/redux/datablocks/types";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import { RequestStatus } from "@/domains/evidence-requests/constants";
import {
  activeTabs,
  DataBlockEditRequestsSurveyModalFooterProps,
  DataBlockRequestsSurveyModalFooterProps,
  DataBlockRequestsTabProps,
  DataBlocksTabProps,
  SurveyDataBlockModalProps,
  ViewTypes,
} from "@/features/survey/form/types";

import "../styles.css";

const RequestFormModalFooter = ({
  formHook,
  onBackToTabs,
}: DataBlockRequestsSurveyModalFooterProps) => {
  const { t } = useTranslation();

  const { currentStep, handleNextStep, handlePreviousStep, isSubmitting } =
    formHook;

  return (
    <div className="survey-data-blocks-modal-footer-buttons">
      {currentStep > 0 && (
        <BaseButton
          variant={BUTTON_VARIANTS.TERTIARY}
          size={BUTTON_SIZES.LARGE}
          onClick={handlePreviousStep}
        >
          {t("survey.labels.back")}
        </BaseButton>
      )}

      {currentStep === 0 && (
        <BaseButton
          variant={BUTTON_VARIANTS.TERTIARY}
          size={BUTTON_SIZES.LARGE}
          onClick={onBackToTabs}
        >
          {t("survey.labels.cancel")}
        </BaseButton>
      )}

      <BaseButton onClick={handleNextStep} loading={isSubmitting}>
        {t(currentStep === 0 ? "survey.labels.next" : "survey.labels.send")}
      </BaseButton>
    </div>
  );
};

const EditRequestFormModalFooter = ({
  formHook,
  onBackToTabs,
}: DataBlockEditRequestsSurveyModalFooterProps) => {
  const { t } = useTranslation();

  const { handleSubmit, isSubmitting } = formHook;

  return (
    <div className="survey-data-blocks-modal-footer-buttons">
      <BaseButton variant="tertiary" size="large" onClick={onBackToTabs}>
        {t("survey.labels.cancel")}
      </BaseButton>

      <BaseButton onClick={handleSubmit} loading={isSubmitting}>
        {t("survey.labels.send")}
      </BaseButton>
    </div>
  );
};

const DataBlocksTab = ({
  filters,
  currentQuestion,
  selectedBlocks,
  setSelectedBlocks,
  onEditRequestClick,
  onCreateDataBlockClick,
}: DataBlocksTabProps) => {
  const { t } = useTranslation();
  const currentUser = useSelector(selectCurrentUser);
  const { canCreate } = useDataBlockPermissions({ currentUser });
  const { data: dataTemplates } = useGetDataTemplatesQuery({
    pagination: "off",
    disabled: false,
    isEvidence: true,
  }) as { data: DataTemplateResponse[] };
  const { isMobile } = useResponsive();

  const allowedDataTemplates = useMemo(() => {
    const filterDataTemplates = filters?.dataTemplates;
    if (!dataTemplates || !filterDataTemplates) return;

    return dataTemplates.filter((template) => {
      return filterDataTemplates.includes(template.id);
    });
  }, [dataTemplates, filters]);

  const formatTemplateList = (items: string[]) => {
    if (items.length === 0) return "";
    if (items.length === 1) return items[0];
    if (items.length === 2) return `${items[0]} and ${items[1]}`;

    const lastItem = items.pop();
    return `${items.join(", ")} and ${lastItem}`;
  };

  return (
    currentUser && (
      <div className="survey-data-blocks-evidence">
        <div className="survey-data-blocks-evidence-header">
          <h2 className="header-s-medium">
            {t("survey.labels.chooseEvidence")}
          </h2>
          {canCreate && (
            <BaseButton
              size="small"
              leadingIcon={<PlusOutlined />}
              onClick={onCreateDataBlockClick}
            >
              {t("survey.labels.createDataBlock")}
            </BaseButton>
          )}
        </div>

        <div className="survey-data-blocks-evidence-content">
          {allowedDataTemplates ? (
            <p className="body-m-regular">
              {allowedDataTemplates.length === 0
                ? t("survey.labels.evidenceTabDescriptionAll")
                : t("survey.labels.evidenceTabDescription", {
                    dataTemplates: formatTemplateList(
                      allowedDataTemplates.map((template) => template.title),
                    ),
                    isPlural:
                      allowedDataTemplates.length === 1
                        ? t("survey.labels.is")
                        : t("survey.labels.are"),
                  })}
            </p>
          ) : null}

          <EvidenceTable
            defaultFilters={{
              organizations: currentUser ? currentUser.organization?.id : null,
              editable: true,
              ...filters,
            }}
            filterNames={["createdBy", "search"]}
            columnNames={[
              "request",
              "title",
              "type",
              "createdBy",
              "updatedAt",
              "actions",
            ]}
            additionalColumns={{
              request: {
                fixed: !isMobile ? "left" : undefined,
                align: "center",
                render: (value) => (
                  <BaseTooltip
                    title={value.request && t("survey.labels.createdViaInvite")}
                  >
                    {value.request != null &&
                      currentQuestion === value.request.question && (
                        <BaseSvgRenderer
                          component={ReceiveInviteIcon}
                          width="20"
                          height="20"
                          stroke="blue"
                        />
                      )}
                  </BaseTooltip>
                ),
              },
              actions: {
                title: t("survey.labels.actions"),
                key: "actions",
                render: (value) => {
                  if (!value.request) return;

                  return (
                    <BaseTooltip
                      title={
                        value.request.status ===
                        RequestStatus.REVISION_NEEDED.value
                          ? t("survey.labels.editAlreadyRequestForDataBlock")
                          : ""
                      }
                    >
                      {value.createdBy.id !== currentUser.id && (
                        <BaseButton
                          size="small"
                          variant="secondary"
                          disabled={
                            value.request.status ===
                            RequestStatus.REVISION_NEEDED.value
                          }
                          onClick={() => onEditRequestClick(value.request.id)}
                        >
                          {t("survey.labels.requestEdit")}
                        </BaseButton>
                      )}
                    </BaseTooltip>
                  );
                },
              },
            }}
            rowSelection={{
              enabled: true,
              multiple: true,
              selectedRows: selectedBlocks || [],
              onSelect: (blocks) => setSelectedBlocks(blocks),
            }}
          />
        </div>
      </div>
    )
  );
};

const DataBlockRequestsTab = ({
  onCreateRequestsClick,
}: DataBlockRequestsTabProps) => {
  const { t } = useTranslation();
  const currentUser = useSelector(selectCurrentUser);

  return (
    currentUser && (
      <div className="survey-data-blocks-evidence">
        <div className="survey-data-blocks-evidence-header">
          <h2 className="header-s-regular">{t("survey.labels.invites")}</h2>
          <BaseButton size="small" onClick={onCreateRequestsClick}>
            <BaseSvgRenderer
              width="20"
              height="20"
              stroke="white"
              component={DataBlockInviteIcon}
            />
            {t("survey.labels.sendRequests")}
          </BaseButton>
        </div>
        <div className="survey-data-blocks-invites-content">
          <p className="body-m-regular">
            {t("survey.labels.invitesTabDescription")}
          </p>

          <RequestsTable
            className="data-block-requests-table"
            filterNames={["search", "dataTemplateType"]}
            columnNames={["user", "type", "status", "dataBlock", "actions"]}
          />
        </div>
      </div>
    )
  );
};

const SurveyDataBlockModal = ({
  open,
  setOpen,
  filters,
  selectedBlocks,
  setSelectedBlocks,
  initialData = {},
}: SurveyDataBlockModalProps) => {
  const { t } = useTranslation();
  const [activeView, setActiveView] = useState(ViewTypes.Tabs);
  const [activeTab, setActiveTab] = useState(activeTabs.dataBlocks);
  const [_selectedBlocks, _setSelectedBlocks] = useState<DataBlockResponse[]>(
    [],
  );
  const [editRequestId, setEditRequestId] = useState<number>();
  const [openCreateBlockModal, setOpenCreateBlockModal] = useState(false);

  useEffect(() => {
    if (!open) {
      setActiveView(ViewTypes.Tabs);
      setActiveTab(activeTabs.dataBlocks);
    }
  }, [open]);

  useEffect(() => {
    _setSelectedBlocks(selectedBlocks || []);
  }, [selectedBlocks]);

  const requestFormHook = useDataBlockRequestForm({
    initialData,
    onSuccess: () => setActiveView(ViewTypes.Tabs),
  });
  const editRequestFormHook = useEditDataBlockRequestForm({
    request: editRequestId,
    onSuccess: () => setActiveView(ViewTypes.Tabs),
  });

  const handleBackToTabs = () => {
    requestFormHook.resetForm();
    editRequestFormHook.form.reset();
    setActiveView(ViewTypes.Tabs);
  };

  const renderFooter = () => {
    if (activeView === ViewTypes.RequestsForm) {
      return (
        <RequestFormModalFooter
          formHook={requestFormHook}
          onBackToTabs={handleBackToTabs}
        />
      );
    } else if (activeView === ViewTypes.EditRequestForm) {
      return (
        <EditRequestFormModalFooter
          formHook={editRequestFormHook}
          onBackToTabs={handleBackToTabs}
        />
      );
    } else if (
      activeView === ViewTypes.Tabs &&
      activeTab === activeTabs.dataBlocks
    ) {
      return (
        <div className="survey-data-blocks-modal-footer-buttons">
          <BaseButton
            disabled={
              _selectedBlocks.length === 0 && selectedBlocks.length === 0
            }
            onClick={() => setSelectedBlocks(_selectedBlocks)}
          >
            {selectedBlocks.length === 0
              ? t("survey.labels.attachEvidence")
              : t("survey.labels.updateEvidence")}
          </BaseButton>
        </div>
      );
    }
    return null;
  };

  const ComponentViews = {
    [ViewTypes.Tabs]: (
      <BaseTabs
        items={[
          {
            label: t("survey.labels.evidence"),
            key: activeTabs.dataBlocks,
            children: (
              <DataBlocksTab
                selectedBlocks={_selectedBlocks}
                setSelectedBlocks={_setSelectedBlocks}
                currentQuestion={initialData?.question}
                filters={filters}
                onEditRequestClick={(requestId) => {
                  setEditRequestId(requestId);
                  setActiveView(ViewTypes.EditRequestForm);
                }}
                onCreateDataBlockClick={() => setOpenCreateBlockModal(true)}
              />
            ),
          },
          {
            label: t("survey.labels.invites"),
            key: activeTabs.requests,
            children: (
              <DataBlockRequestsTab
                onCreateRequestsClick={() =>
                  setActiveView(ViewTypes.RequestsForm)
                }
              />
            ),
          },
        ]}
        onChange={(key) => setActiveTab(key as activeTabs)}
        defaultActiveKey={activeTabs.dataBlocks}
      />
    ),
    [ViewTypes.RequestsForm]: <RequestForm formHook={requestFormHook} />,
    [ViewTypes.EditRequestForm]: (
      <EditRequestForm formHook={editRequestFormHook} />
    ),
  };

  const modalTitle = useMemo(() => {
    if (activeView === ViewTypes.Tabs) {
      return t("survey.labels.dataBlock");
    } else {
      return (
        <div className="survey-data-block-modal-title-container">
          <span onClick={handleBackToTabs}>
            <LeftOutlined style={{ fontSize: 16 }} />
          </span>
          {activeView === ViewTypes.EditRequestForm
            ? t("survey.labels.editRequest")
            : t("survey.labels.inviteUsers")}
        </div>
      );
    }
  }, [activeView]);

  return (
    <BaseModal
      title={modalTitle || ""}
      open={open}
      onCancel={() => setOpen(false)}
      width={1000}
      footer={renderFooter()}
    >
      {ComponentViews[activeView]}

      <CreateDataBlock
        open={openCreateBlockModal}
        setOpen={() => setOpenCreateBlockModal(false)}
        filters={filters}
      />
    </BaseModal>
  );
};

export default SurveyDataBlockModal;
